import { useEffect } from 'react';

/**
 * Set per-page <title> and <meta name="description"> + open-graph siblings.
 *
 * Lightweight, dependency-free (no react-helmet). Restores the previous values
 * on unmount so SPA navigation back to other routes does not bleed metadata.
 *
 * @param {Object}  opts
 * @param {string}  opts.title         Page title (full string, e.g. "About | BlueLock Freights").
 * @param {string}  opts.description   Meta description (~150-160 chars max recommended).
 * @param {string=} opts.ogTitle       Optional override for og:title (defaults to title).
 * @param {string=} opts.ogDescription Optional override for og:description (defaults to description).
 */
const upsertMeta = (selector, attr, attrValue, content) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
  return el;
};

const useDocumentTitle = ({ title, description, ogTitle, ogDescription } = {}) => {
  useEffect(() => {
    if (!title && !description) return;

    const prevTitle = document.title;
    const descEl = document.head.querySelector('meta[name="description"]');
    const ogTitleEl = document.head.querySelector('meta[property="og:title"]');
    const ogDescEl = document.head.querySelector('meta[property="og:description"]');

    const prevDesc = descEl ? descEl.getAttribute('content') : null;
    const prevOgTitle = ogTitleEl ? ogTitleEl.getAttribute('content') : null;
    const prevOgDesc = ogDescEl ? ogDescEl.getAttribute('content') : null;

    if (title) document.title = title;
    if (description) upsertMeta('meta[name="description"]', 'name', 'description', description);
    if (title || ogTitle) {
      upsertMeta('meta[property="og:title"]', 'property', 'og:title', ogTitle || title);
    }
    if (description || ogDescription) {
      upsertMeta(
        'meta[property="og:description"]',
        'property',
        'og:description',
        ogDescription || description
      );
    }

    return () => {
      document.title = prevTitle;
      if (prevDesc !== null) upsertMeta('meta[name="description"]', 'name', 'description', prevDesc);
      if (prevOgTitle !== null) upsertMeta('meta[property="og:title"]', 'property', 'og:title', prevOgTitle);
      if (prevOgDesc !== null) upsertMeta('meta[property="og:description"]', 'property', 'og:description', prevOgDesc);
    };
  }, [title, description, ogTitle, ogDescription]);
};

export default useDocumentTitle;
